import React from 'react';

interface AddMemberModalProps {
  show: boolean;
  newMember: {
    name: string;
    phone: string;
    email: string;
    gender: string;
    photoUrl: string;
    membershipType: 'Monthly' | 'Quarterly' | 'Yearly';
    plan: string;
    joinDate: string;
    expiryDate: string;
    paymentMethod: 'Cash' | 'Card' | 'UPI';
    paymentStatus: 'Paid' | 'Pending';
    invoiceNumber: string;
    height: string;
    weight: string;
    medical: string;
    fitnessGoal: 'Weight Loss' | 'Muscle Gain' | 'General Fitness';
    emergencyName: string;
    emergencyNumber: string;
    rulesAgreement: boolean;
    termsAgreement: boolean;
  };
  onChange: (field: string, value: string | number | boolean) => void;
  onClose: () => void;
  onAdd: () => void;
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({ show, newMember, onChange, onClose, onAdd }) => {

  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-8 overflow-y-auto max-h-[90vh]">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 tracking-tight">Add New Member</h3>
            <p className="text-gray-600 font-medium mt-1">Complete all details for registration</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-2 hover:bg-gray-100 rounded-lg"
          >
            ✕
          </button>
        </div>
        <form className="space-y-6" onSubmit={e => { e.preventDefault(); onAdd(); }}>
          {/* Personal Details */}
          <div>
            <h4 className="font-bold text-lg mb-2">🧑 Personal Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Full Name</label>
                <input type="text" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" value={newMember.name} onChange={e => onChange('name', e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Contact Number</label>
                <input type="tel" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" value={newMember.phone} onChange={e => onChange('phone', e.target.value)} placeholder="+91-XXXXXXXXXX" required />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Email Address</label>
                <input type="email" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" value={newMember.email} onChange={e => onChange('email', e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Gender</label>
                <select className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" value={newMember.gender} onChange={e => onChange('gender', e.target.value)} required>
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">Photo Upload (Selfie/ID Proof)</label>
                <input type="file" accept="image/*" className="w-full" onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = ev => onChange('photoUrl', ev.target?.result as string);
                    reader.readAsDataURL(file);
                  }
                }} />
                {newMember.photoUrl && <img src={newMember.photoUrl} alt="Preview" className="mt-2 h-20 rounded" />}
              </div>
            </div>
          </div>
          {/* Membership & Payment */}
          <div>
            <h4 className="font-bold text-lg mb-2">💳 Membership & Payment</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Membership Type</label>
                <select className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" value={newMember.membershipType} onChange={e => onChange('membershipType', e.target.value as any)} required>
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Yearly">Yearly</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Plan Selected</label>
                <input type="text" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" value={newMember.plan} onChange={e => onChange('plan', e.target.value)} placeholder="Basic/Premium/PT etc." required />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Joining Date</label>
                <input type="date" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" value={newMember.joinDate} onChange={e => onChange('joinDate', e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Expiry Date</label>
                <input type="date" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" value={newMember.expiryDate} onChange={e => onChange('expiryDate', e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Payment Method</label>
                <select className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" value={newMember.paymentMethod} onChange={e => onChange('paymentMethod', e.target.value as any)} required>
                  <option value="Cash">Cash</option>
                  <option value="Card">Card</option>
                  <option value="UPI">UPI</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Payment Status</label>
                <select className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" value={newMember.paymentStatus} onChange={e => onChange('paymentStatus', e.target.value as any)} required>
                  <option value="Paid">Paid</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">Invoice/Receipt Number</label>
                <input type="text" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" value={newMember.invoiceNumber} onChange={e => onChange('invoiceNumber', e.target.value)} required />
              </div>
            </div>
          </div>
          {/* Health & Fitness */}
          <div>
            <h4 className="font-bold text-lg mb-2">🏋️ Health & Fitness</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Height (cm)</label>
                <input type="number" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" value={newMember.height} onChange={e => onChange('height', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Weight (kg)</label>
                <input type="number" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" value={newMember.weight} onChange={e => onChange('weight', e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">Medical Conditions (If any)</label>
                <input type="text" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" value={newMember.medical} onChange={e => onChange('medical', e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">Fitness Goals</label>
                <select className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" value={newMember.fitnessGoal} onChange={e => onChange('fitnessGoal', e.target.value as any)}>
                  <option value="Weight Loss">Weight Loss</option>
                  <option value="Muscle Gain">Muscle Gain</option>
                  <option value="General Fitness">General Fitness</option>
                </select>
              </div>
            </div>
          </div>
          {/* Emergency Contact */}
          <div>
            <h4 className="font-bold text-lg mb-2">🚨 Emergency Contact</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Emergency Contact Name</label>
                <input type="text" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" value={newMember.emergencyName} onChange={e => onChange('emergencyName', e.target.value)} />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Emergency Contact Number</label>
                <input type="tel" className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl" value={newMember.emergencyNumber} onChange={e => onChange('emergencyNumber', e.target.value)} placeholder="+91-XXXXXXXXXX" />
              </div>
            </div>
          </div>
          {/* Legal & Consent */}
          <div>
            <h4 className="font-bold text-lg mb-2">✅ Legal & Consent</h4>
            <div className="flex flex-col gap-2">
              <label className="inline-flex items-center">
                <input type="checkbox" className="mr-2" checked={newMember.rulesAgreement} onChange={e => onChange('rulesAgreement', e.target.checked)} />
                Gym Rules & Waiver Agreement
              </label>
              <label className="inline-flex items-center">
                <input type="checkbox" className="mr-2" checked={newMember.termsAgreement} onChange={e => onChange('termsAgreement', e.target.checked)} />
                Terms & Conditions Agreement
              </label>
            </div>
          </div>
          <div className="flex gap-4 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gray-100 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors duration-200 font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors duration-200 font-semibold shadow-sm"
            >
              Add Member
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMemberModal;
